import Button from '@rio-cloud/rio-uikit/Button';
import OnboardingTip from '@rio-cloud/rio-uikit/OnboardingTip';
import TableSettingsDialog from '@rio-cloud/rio-uikit/TableSettingsDialog';
import useLocalStorage from '@rio-cloud/rio-uikit/useLocalStorage';
import { FunctionComponent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTableColumnSettings } from './TableColumnSettingsState';
import { getDefaultTableColumnSettings, TableColumnProperties } from './tableSettings';

export const TableColumnSettings: FunctionComponent = () => {
    const intl = useIntl();
    const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [showOnboarding, setShowOnboarding] = useLocalStorage('onboarding.tableSettings.button', 'true');

    const handleToggleTableSettingsDialog = () => setShowTableSettingsDialog(!showTableSettingsDialog);

    const { columnDefinition, tableColumnSettings, setTableColumnSettings } = useTableColumnSettings();

    const disabledColumns = columnDefinition.filter(({ alwaysVisible }) => alwaysVisible).map(({ column }) => column);
    const columnLabels = Object.fromEntries(
        columnDefinition.map(({ column, labelId }) => [
            column,
            labelId !== undefined ? <FormattedMessage key={labelId} id={labelId} /> : '',
        ]),
    );

    const defaultSettings = getDefaultTableColumnSettings(columnDefinition);
    const defaultColumns = getColumnNames(defaultSettings);
    const defaultHiddenColumns = getColumnNames(defaultSettings.filter(({ visible }) => !visible));

    const columns = getColumnNames(tableColumnSettings);
    const hiddenColumns = getColumnNames(tableColumnSettings.filter(({ visible }) => !visible));

    const handleColumnChange = (newColumnOrder: string[], newHiddenColumns: string[]) => {
        const newTableColumnSettings: TableColumnProperties<string> = newColumnOrder.map((columnName) => ({
            column: columnName,
            visible: !newHiddenColumns.includes(columnName),
        }));
        setTableColumnSettings(newTableColumnSettings);
    };

    return (
        <>
            <OnboardingTip
                className="z-index-10"
                show={showOnboarding === 'true'}
                onHide={() => setShowOnboarding('false')}
                placement={OnboardingTip.BOTTOM_START}
                width={300}
                title={intl.formatMessage({
                    id: 'outboundOrderBook.common.tableColumnSettings.title',
                })}
                content={intl.formatMessage({
                    id: 'outboundOrderBook.common.tableColumnSettings.onboarding.content',
                })}
            >
                <Button
                    iconOnly
                    iconName="rioglyph-settings"
                    onClick={handleToggleTableSettingsDialog}
                    aria-label={intl.formatMessage({
                        id: 'outboundOrderBook.common.tableColumnSettings.title',
                    })}
                />
            </OnboardingTip>
            <TableSettingsDialog
                show={showTableSettingsDialog}
                title={<FormattedMessage id="outboundOrderBook.common.tableColumnSettings.title" />}
                onHide={handleToggleTableSettingsDialog}
                onColumnChange={handleColumnChange}
                defaultColumnOrder={defaultColumns}
                columnOrder={columns}
                columnLabels={columnLabels}
                disabledColumns={disabledColumns}
                defaultHiddenColumns={defaultHiddenColumns}
                hiddenColumns={hiddenColumns}
                closeButtonText={<FormattedMessage id="outboundOrderBook.common.button.close" />}
                resetButtonText={<FormattedMessage id="outboundOrderBook.common.tableColumnSettings.resetColumns" />}
                searchPlaceholder={intl.formatMessage({
                    id: 'outboundOrderBook.common.tableColumnSettings.searchPlaceholder',
                })}
                notFoundMessage={intl.formatMessage({
                    id: 'outboundOrderBook.common.tableColumnSettings.emptySearchResult',
                })}
                autoLabel={<FormattedMessage id="outboundOrderBook.common.automaticColumnWidth" />}
                columnSearchValue={searchText}
                onSearchChange={setSearchText}
                immediateChange
            />
        </>
    );
};

const getColumnNames = (columns: TableColumnProperties<string>) => columns.map(({ column }) => column);
